import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { CustomDialog } from "../../../components/StyledComponents";
import {
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  RadioGroup,
  FormControlLabel,
  Radio,
} from "@mui/material";
import {
  ControlpanelPrimaryButton,
  ControlpanelSecondaryButton,
  GrayIconButton,
} from "../../../components/StyledButton";
import CloseIcon from "@mui/icons-material/Close";
import SaveIcon from "@mui/icons-material/Save";
import CustomDateTimeRangePicker from "../../../components/CustomDateTimeRangePicker";
import CustomSelect from "../../../components/CustomSelect";
import CustomInputHorizontal from "../../../components/CustomInputHorizontal";
import HolidayApi from "./../../../axios/LeaveAndHoliday/HolidayApi";
import MonthsData from "../../../assets/data/MonthData";
import DaysOfWeekData from "../../../assets/data/DaysOfWeekData";
import DaysOfMonthData from "../../../assets/data/DaysOfMonthData";
import { toast } from "react-toastify";
import { format, isBefore, startOfDay } from "date-fns";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import { useAuth } from "../../../context/AuthContext";
dayjs.extend(utc);
dayjs.extend(utc);

const EditHolidayDialog = (props) => {
  const { t } = useTranslation();
  const {
    open,
    handleClose,
    onRefresh,
    selectedHoliday, // holiday cần edit (đã chọn)
  } = props;

  // 👉 Giả định API có updateHoliday
  const { editHoliday } = HolidayApi();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { genaralSettingTimeZone } = useAuth()

  function toUTCStartOfDayByOffset(date, offset) {
    if (!date) return null;

    return dayjs(date)
      .hour(0)
      .minute(0)
      .second(0)
      .millisecond(0)
      .utcOffset(offset, true) // 👈 giữ local time, áp offset
      .utc()
      .format("YYYY-MM-DDTHH:mm:ss[Z]");
  }

  function toUTCEndOfDayByOffset(date, offset) {
    if (!date) return null;

    return dayjs(date)
      .hour(23)
      .minute(59)
      .second(59)
      .millisecond(0)
      .utcOffset(offset, true)
      .utc()
      .format("YYYY-MM-DDTHH:mm:ss[Z]");
  }

  const recurringTypeData = useMemo(
    () => [
      { value: "weekly", label: t("common.weekly") },
      { value: "monthly", label: t("common.monthly") },
      { value: "yearly", label: t("common.yearly") },
    ],
    [t]
  );

  const monthData = MonthsData();
  const weekDayData = DaysOfWeekData();
  const monthDayData = DaysOfMonthData();

  // ---- Helpers giống AddHolidayDialog ----
  const toDate = (val) => {
    const v = val && val.target && val.target.value ? val.target.value : val;
    if (dayjs.isDayjs(v)) return v.toDate();
    if (typeof v === "string") return new Date(v);
    return v instanceof Date ? v : new Date(v);
  };

  const initialFormData = useMemo(() => {
    if (!selectedHoliday) {
      const now = new Date();
      const start = new Date(format(now, "yyyy-MM-dd 00:00:00"));
      const end = new Date(format(now, "yyyy-MM-dd 23:59:00"));
      return {
        isHolidayRecurring: false,
        recurringType: "weekly",
        day: "monday",
        month: "january",
        normalDateFrom: start,
        normalDateTo: end,
        startFrom: start,
        endsOn: end,
        description: "",
        holidayId: null,
        startDateEffective: start,
        endDateEffective: end,
      };
    }

    const {
      isHolidayRecurring,
      recurringType,
      recurringDay,
      recurringMonth,
      startDate,
      endDate,
      description,
      holidayId,
      startDateEffective,
      endDateEffective
    } = selectedHoliday;

    const s = startDate ? dayjs.utc(startDate).local().toDate() : null;
    const e = endDate ? dayjs.utc(endDate).local().toDate() : null;

    if (!isHolidayRecurring) {
      const nStart = s || new Date(format(new Date(), "yyyy-MM-dd 00:00:00"));
      const nEnd = e || new Date(format(new Date(), "yyyy-MM-dd 23:59:00"));
      return {
        isHolidayRecurring: false,
        recurringType: "weekly",
        day: "monday",
        month: "january",
        normalDateFrom: nStart,
        normalDateTo: nEnd,
        startFrom: nStart,
        endsOn: nEnd,
        description: description || "",
        holidayId: holidayId ?? null,
      };
    }

    // Recurring
    return {
      isHolidayRecurring: true,
      recurringType: recurringType,
      day: recurringDay,
      month: recurringMonth,
      startFrom: s,
      endsOn: e,
      normalDateFrom: s || new Date(format(new Date(), "yyyy-MM-dd 00:00:00")),
      normalDateTo: e || new Date(format(new Date(), "yyyy-MM-dd 23:59:00")),
      description: description || "",
      holidayId: holidayId ?? null,
      startDateEffective: startDateEffective ? dayjs.utc(startDateEffective).local().toDate() : null,
      endDateEffective: endDateEffective ? dayjs.utc(endDateEffective).local().toDate() : null,
    };
  }, [selectedHoliday]);

  const [formData, setFormData] = useState(initialFormData);
  const [startFromEnabled, setStartFromEnabled] = useState(
    initialFormData.isHolidayRecurring ? true : true
  );
  const [endsOnEnabled, setEndsOnEnabled] = useState(
    initialFormData.isHolidayRecurring ? true : true
  );
  const [isInputValid, setIsInputValid] = useState(true);
  const [validationMessages, setValidationMessages] = useState({
    endDate: "",
    description: "",
  });

  useEffect(() => {
    setFormData(initialFormData);
    // Nếu recurring: bật/tắt radio dựa theo có giá trị hay không
    if (initialFormData.isHolidayRecurring) {
      setStartFromEnabled(!!initialFormData.startFrom);
      setEndsOnEnabled(!!initialFormData.endsOn);
    } else {
      setStartFromEnabled(true);
      setEndsOnEnabled(true);
    }
  }, [initialFormData]);

  const getStartEnd = (fd) => {
    if (!fd.isHolidayRecurring) {
      return { start: toDate(fd.normalDateFrom), end: toDate(fd.normalDateTo) };
    }
    const s = startFromEnabled ? toDate(fd.startFrom) : null;
    const e = endsOnEnabled ? toDate(fd.endsOn) : null;
    return { start: s, end: e };
  };

  // Validate end >= start (nếu cả 2 có)
  useEffect(() => {
    const { start, end } = getStartEnd(formData);
    if (start && end && isBefore(startOfDay(end), startOfDay(start))) {
      setValidationMessages((m) => ({ ...m, endDate: t("common.invalid-date") }));
      setIsInputValid(false);
    } else {
      setValidationMessages((m) => ({ ...m, endDate: "" }));
      setIsInputValid(true);
    }
  }, [
    formData.isHolidayRecurring,
    formData.normalDateFrom,
    formData.normalDateTo,
    formData.startFrom,
    formData.endsOn,
    startFromEnabled,
    endsOnEnabled,
    t,
  ]);

  // Description validate (giống AddHolidayDialog)
  useEffect(() => {
    if (!formData.description || formData.description.trim() === "") {
      setValidationMessages((m) => ({ ...m, description: t("common.required") }));
    } else {
      setValidationMessages((m) => ({ ...m, description: "" }));
    }
  }, [formData.description, t]);

  const handleChangeIsRecurring = (e) => {
    const value = e.target.value === "true";
    setFormData((prev) => ({
      ...prev,
      isHolidayRecurring: value,
    }));
  };

  const handleChangeStartRadio = (event) => {
    const value = event.target.value === "true";
    setStartFromEnabled(value);

    if (!value) {
      setFormData((prev) => ({
        ...prev,
        startFrom: null, // 👈 clear luôn
      }));
    }
  };

  const handleChangeEndRadio = (event) => {
    const value = event.target.value === "true";
    setEndsOnEnabled(value);

    if (!value) {
      setFormData((prev) => ({
        ...prev,
        endsOn: null, // 👈 clear luôn
      }));
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type } = e.target || {};
    let newValue;

    if (type === "radio") {
      newValue = value === "true";
    } else {
      newValue = e?.target?.value ?? e;
    }

    setFormData((prev) => {
      const updated = { ...prev, [name]: newValue };
      return updated;
    });
  };

  const handleNormalDateRangeChange = ([start, end]) => {
    if (!start || !end) return;
    setFormData((prev) => ({
      ...prev,
      normalDateFrom: start,
      normalDateTo: end,
    }));
  };

  const handleRecurringDateRangeChange = ([start, end]) => {
    if (!start || !end) return;
    setFormData((prev) => ({
      ...prev,
      startFrom: start,
      endsOn: end,
    }));
  };

  const mapFormDataToApiData = (data) => {
    const payloadBase = {
      holidayId: data.holidayId,
      description: data.description,
      isHolidayRecurring: data.isHolidayRecurring,
      recurringType: "",
      recurringDay: "",
      recurringMonth: "",
      startDate: null,
      endDate: null,
    };

    if (!data.isHolidayRecurring) {
      return {
        ...payloadBase,
        startDate: toUTCStartOfDayByOffset(
          data.normalDateFrom,
          genaralSettingTimeZone
        ),
        endDate: toUTCEndOfDayByOffset(
          data.normalDateTo,
          genaralSettingTimeZone
        ),
      };
    }

    const startDate = startFromEnabled
      ? toUTCStartOfDayByOffset(data.startFrom, genaralSettingTimeZone)
      : null;

    const endDate = endsOnEnabled
      ? toUTCEndOfDayByOffset(data.endsOn, genaralSettingTimeZone)
      : null;

    if (data.recurringType === "weekly" || data.recurringType === "monthly") {
      return {
        ...payloadBase,
        startDate,
        endDate,
        recurringType: data.recurringType,
        recurringDay: data.day,
      };
    }

    return {
      ...payloadBase,
      startDate,
      endDate,
      recurringType: data.recurringType,
      recurringDay: data.day,
      recurringMonth: data.month,
    };
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!isInputValid || validationMessages.description) return;

    if (!isYearlyDateInRange(formData)) {
      toast.warning(t("common.recurring-start-end"));
      return;
    }

    setIsSubmitting(true);
    try {
      const payload = mapFormDataToApiData(formData);
      payload.langId = localStorage.getItem("i18nextLngId");

      const res = await editHoliday(payload);
      if (res?.data?.responseCode === 0 || res?.data?.message === "Thành công" || res?.data?.message === "Successful") {
        toast.success(t("common.edit-holiday-successfully"));
        handleClose();
        onRefresh?.();
      } else {
        toast.error(res?.data?.message);
      }
    } catch (err) {
      toast.error(err?.response?.data?.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const onCloseDialog = () => {
    handleClose?.();
  };

  useEffect(() => {
    setStartFromEnabled(!!formData.startFrom);
  }, [formData.startFrom]);

  useEffect(() => {
    setEndsOnEnabled(!!formData.endsOn);
  }, [formData.endsOn]);

  const isYearlyDateInRange = (data) => {
    if (data.recurringType !== "yearly") return true;

    // nếu không giới hạn khoảng → bỏ qua
    if (!startFromEnabled || !endsOnEnabled) return true;

    const start = toDate(data.startFrom);
    const end = toDate(data.endsOn);

    if (!start || !end) return true;

    const monthIndex = monthData.findIndex((m) => m.value === data.month);
    const day = Number(data.day);

    if (monthIndex === -1 || !day) return false;

    const startYear = start.getFullYear();
    const endYear = end.getFullYear();

    // 🔥 CHECK TỪNG NĂM
    for (let year = startYear; year <= endYear; year++) {
      const holidayDate = new Date(year, monthIndex, day);

      // skip invalid date (vd: 30/02)
      if (holidayDate.getMonth() !== monthIndex) continue;

      if (
        !isBefore(holidayDate, startOfDay(start)) &&
        !isBefore(startOfDay(end), startOfDay(holidayDate))
      ) {
        return true; // ✅ chỉ cần 1 năm hợp lệ là OK
      }
    }

    return false;
  };

  return (
    <CustomDialog maxWidth={"sm"} open={open} loading={isSubmitting} onClose={onCloseDialog} keepMounted>
      <DialogTitle>
        <div className="flex justify-between">
          <div className="flex items-center gap-4">
            <p className="text-xl font-semibold ">{t("common.edit-holiday")}</p>
          </div>
          <GrayIconButton onClick={onCloseDialog} className="self-start" disabled={isSubmitting}>
            <CloseIcon />
          </GrayIconButton>
        </div>
      </DialogTitle>

      <DialogContent>
        {/* Mode chọn Normal / Recurring */}
        <div className="pt-5 grid grid-cols-6 md:col-span-1 items-center">
          <div className="col-span-2">{t("common.end-of-repitition")}</div>
          <div className="col-span-4 flex justify-end items-start">
            <FormControl component="fieldset">
              <RadioGroup
                row
                name="isHolidayRecurring"
                value={formData.isHolidayRecurring}
                onChange={handleChangeIsRecurring}
              >
                <FormControlLabel
                  value={false}
                  control={<Radio size="small" />}
                  label={t("common.normal")}
                />
                <FormControlLabel
                  sx={{ marginLeft: "50px", marginRight: "unset" }}
                  value={true}
                  control={<Radio size="small" />}
                  label={t("common.recurring")}
                />
              </RadioGroup>
            </FormControl>
          </div>
        </div>

        {/* Description */}
        <div className="grid grid-cols-6 items-center">
          <div className="col-span-6">
            <CustomInputHorizontal
              required={true}
              title={t("common.description")}
              inputType="text"
              name="description"
              placeholder={t("common.description")}
              handleInputChange={handleInputChange}
              value={formData.description}
            />
            {validationMessages.description && (
              <p className="mt-1 text-xs text-[#DC2626]">
                {validationMessages.description}
              </p>
            )}
          </div>
        </div>

        {/* Normal */}
        {!formData.isHolidayRecurring && (
          <div className="pt-[10px] grid grid-cols-6 items-center">
            <div className="col-span-2 flex">
              <p>
                {t("common.from")} - {t("common.to")}
              </p>
            </div>
            <div className="col-span-4">
              <CustomDateTimeRangePicker
                mode="range"
                value={[formData.normalDateFrom, formData.normalDateTo]}
                disableTime
                showOnlyDate
                onChange={handleNormalDateRangeChange}
              />
              {validationMessages.endDate && (
                <p className="mt-1 text-xs text-[#DC2626]">
                  {validationMessages.endDate}
                </p>
              )}
            </div>
          </div>
        )}

        {/* Recurring */}
        {formData.isHolidayRecurring && (
          <>
            {/* Recurring type */}
            <div className="pt-[10px] grid grid-cols-6 items-center">
              <div className="col-span-2">
                <p>{t("common.recurring-type")}</p>
              </div>
              <div className="col-span-4">
                <FormControl className="w-full">
                  <CustomSelect
                    name="recurringType"
                    id="recurringType"
                    value={formData.recurringType}
                    onChange={handleInputChange}
                    options={recurringTypeData}
                    dataType="recurringType"
                    defaultValue={recurringTypeData[0].value}
                  />
                </FormControl>
              </div>
            </div>

            {/* Day (weekly/monthly) */}
            <div className="pt-[10px] grid grid-cols-6 items-center">
              <div className="col-span-2">
                <p>{t("common.day")}</p>
              </div>
              <div className="col-span-4">
                <FormControl className="w-full">
                  {formData.recurringType === "weekly" ? (
                    <CustomSelect
                      name="day"
                      id="weekDay"
                      value={formData.day}
                      onChange={handleInputChange}
                      options={weekDayData}
                      defaultValue={weekDayData[0].value}
                    />
                  ) : (
                    <CustomSelect
                      name="day"
                      id="monthDay"
                      value={formData.day}
                      onChange={handleInputChange}
                      options={monthDayData}
                      defaultValue={monthDayData[0].value}
                    />
                  )}
                </FormControl>
              </div>
            </div>

            {/* Month (yearly) */}
            {formData.recurringType === "yearly" && (
              <div className="pt-[10px] grid grid-cols-6 items-center">
                <div className="col-span-2">
                  <p>{t("common.month")}</p>
                </div>
                <div className="col-span-4">
                  <FormControl className="w-full">
                    <CustomSelect
                      name="month"
                      id="month"
                      value={formData.month}
                      onChange={handleInputChange}
                      options={monthData}
                      dataType="month"
                      defaultValue={monthData[0].value}
                    />
                  </FormControl>
                </div>
              </div>
            )}

            {/* Start */}
            <div className="pt-[10px] grid grid-cols-6">
              <div className="col-span-2 mt-3">{t("common.start")}</div>
              <div className="col-span-4">
                <FormControl component="fieldset">
                  <RadioGroup
                    row
                    name="startFromRad"
                    value={startFromEnabled}
                    onChange={handleChangeStartRadio}
                  >
                    <FormControlLabel
                      value={false}
                      control={<Radio size="small" />}
                      label={t("common.no-specified")}
                    />
                    <FormControlLabel
                      value={true}
                      control={<Radio size="small" />}
                      label={t("common.start-from")}
                    />
                  </RadioGroup>
                </FormControl>
                {startFromEnabled && !endsOnEnabled && (
                  <div>
                    <CustomDateTimeRangePicker
                      mode="single"
                      value={formData.startFrom}
                      disableTime
                      showOnlyDate
                      onChange={(newValue) =>
                        handleInputChange({
                          target: { name: "startFrom", value: newValue },
                        })
                      }
                    />
                  </div>
                )}
              </div>
            </div>

            {/* End */}
            <div className="pt-[10px] grid grid-cols-6">
              <div className="col-span-2 mt-3">{t("common.end")}</div>
              <div className="col-span-4">
                <FormControl component="fieldset">
                  <RadioGroup
                    row
                    name="endsOnRad"
                    value={endsOnEnabled}
                    onChange={handleChangeEndRadio}
                  >
                    <FormControlLabel
                      value={false}
                      control={<Radio size="small" />}
                      label={t("common.no-specified")}
                    />
                    <FormControlLabel
                      value={true}
                      control={<Radio size="small" />}
                      label={t("common.ends-on")}
                    />
                  </RadioGroup>
                </FormControl>
                {!startFromEnabled && endsOnEnabled && (
                  <div>
                    <CustomDateTimeRangePicker
                      mode="single"
                      value={formData.endsOn}
                      disableTime
                      showOnlyDate
                      onChange={(newValue) =>
                        handleInputChange({
                          target: { name: "endsOn", value: newValue },
                        })
                      }
                    />
                  </div>
                )}
                {startFromEnabled && endsOnEnabled && (
                  <div className="pt-2">
                    <CustomDateTimeRangePicker
                      mode="range"
                      value={[formData.startFrom, formData.endsOn]}
                      disableTime
                      showOnlyDate
                      onChange={handleRecurringDateRangeChange}
                    />
                  </div>
                )}
                {validationMessages.endDate && (
                  <p className="mt-1 text-xs text-[#DC2626]">
                    {validationMessages.endDate}
                  </p>
                )}
              </div>
            </div>
          </>
        )}
      </DialogContent>

      <DialogActions>
        <div className="flex gap-2 px-4 py-4">
          <ControlpanelSecondaryButton onClick={onCloseDialog} disabled={isSubmitting}>
            <p>{t("button.cancel")}</p>
          </ControlpanelSecondaryButton>
          <ControlpanelPrimaryButton
            onClick={onSubmit}
            disabled={isSubmitting || !isInputValid || !!validationMessages.description}
          >
            <p>{t("button.save")}</p>
          </ControlpanelPrimaryButton>
        </div>
      </DialogActions>
    </CustomDialog>
  );
};

export default EditHolidayDialog;
